import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MEDICAL_RECORD_SECTIONS,
  type MedicalRecordSection,
} from "../constants/medicalRecordSections";

const STORAGE_KEY = "medical-records-quick-view-preferences";
const DEFAULT_SECTIONS = ["timeline", "vitals", "medical-history"];

export const useMedicalRecordsPreferences = () => {
  const [selectedSections, setSelectedSections] = useState<string[]>([]);

  // Compute sorted section configurations based on selectedSections
  const selectedSectionConfigs = useMemo(() => {
    return selectedSections
      .map((sectionKey) =>
        MEDICAL_RECORD_SECTIONS.find((section) => section.key === sectionKey),
      )
      .filter(
        (section): section is MedicalRecordSection => section !== undefined,
      )
      .sort((a, b) => {
        // Timeline should always come last
        if (a.key === "timeline" && b.key !== "timeline") return 1;
        if (b.key === "timeline" && a.key !== "timeline") return -1;
        // For all other sections, maintain the original order from selectedSections
        return (
          selectedSections.indexOf(a.key) - selectedSections.indexOf(b.key)
        );
      });
  }, [selectedSections]);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSelectedSections(parsed);
          return;
        }
      }
    } catch (error) {
      // Silently fail and use defaults if localStorage is not available or corrupted
    }

    // Use default sections if nothing stored or parsing failed
    setSelectedSections(DEFAULT_SECTIONS);
  }, []);

  // Update preferences in localStorage and state
  const updateSelectedSections = useCallback((newSections: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSections));
      setSelectedSections(newSections);
    } catch (error) {
      // Silently fail if localStorage is not available, but still update state
      setSelectedSections(newSections);
    }
  }, []);

  // Clear preferences and return to defaults
  const resetToDefaults = useCallback(() => {
    updateSelectedSections(DEFAULT_SECTIONS);
  }, [updateSelectedSections]);

  return {
    selectedSections,
    selectedSectionConfigs,
    updateSelectedSections,
    resetToDefaults,
  };
};
